import { useId, useState } from 'react';
import { ThumbsDownIcon, ThumbsUpIcon } from 'lucide-react';
import {
  MESSAGE_FEEDBACK_COMMENT_MAX_LENGTH,
  MESSAGE_FEEDBACK_REASONS,
  type MessageFeedbackReason,
} from '@shared/schemas/message-feedback';
import { MessageAction } from '@/components/ai-elements/message';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useChatMessageFeedback } from '../chat-message-feedback';

const MESSAGE_FEEDBACK_REASON_LABELS: Record<MessageFeedbackReason, string> = {
  incorrect_answer: 'Réponse inexacte',
  incorrect_citation: 'Source ou citation incorrecte',
  missing_information: 'Information manquante',
  not_helpful: 'Pas utile',
  other: 'Autre',
};

const ACTION_CLASS_NAME = 'size-8 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground aria-pressed:text-foreground';

/**
 * Thumbs up / down on an assistant answer. A thumbs down asks why (wrong answer,
 * wrong citation…): the reason feeds the RAG evaluation set. Pressing the
 * active thumb again withdraws the opinion.
 */
export function ChatMessageFeedback({ messageId }: { messageId: string }) {
  const feedback = useChatMessageFeedback();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [reason, setReason] = useState<MessageFeedbackReason | null>(null);
  const [comment, setComment] = useState('');
  const formId = useId();
  if (!feedback) {
    return null;
  }

  const current = feedback.feedbackByMessageId.get(messageId);
  const isUp = current?.rating === 'up';
  const isDown = current?.rating === 'down';

  function openForm(open: boolean) {
    if (open) {
      setReason(current?.reason ?? null);
      setComment('');
    }
    setIsFormOpen(open);
  }

  function submitNegative() {
    feedback!.submit(messageId, {
      comment: comment.trim() || undefined,
      rating: 'down',
      reason: reason ?? undefined,
    });
    setIsFormOpen(false);
  }

  return (
    <>
      <MessageAction
        aria-pressed={isUp}
        className={ACTION_CLASS_NAME}
        label={isUp ? 'Retirer « Réponse utile »' : 'Réponse utile'}
        onClick={() => (isUp ? feedback.clear(messageId) : feedback.submit(messageId, { rating: 'up' }))}
        tooltip="Réponse utile"
      >
        <ThumbsUpIcon className={cn('size-3.5', isUp && 'fill-current')} />
      </MessageAction>
      <Popover open={isFormOpen} onOpenChange={openForm}>
        <PopoverTrigger asChild>
          <MessageAction
            aria-pressed={isDown}
            className={ACTION_CLASS_NAME}
            label={isDown ? 'Modifier mon retour négatif' : 'Réponse à améliorer'}
          >
            <ThumbsDownIcon className={cn('size-3.5', isDown && 'fill-current')} />
          </MessageAction>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 space-y-3 p-3" data-testid="message-feedback-form">
          <p className="text-sm font-medium text-foreground" id={`${formId}-title`}>Qu’est-ce qui n’allait pas ?</p>
          <div aria-labelledby={`${formId}-title`} className="flex flex-wrap gap-1.5" role="radiogroup">
            {MESSAGE_FEEDBACK_REASONS.map((value) => (
              <button
                key={value}
                aria-checked={reason === value}
                className={cn(
                  'rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  reason === value
                    ? 'border-foreground/40 bg-foreground text-background'
                    : 'border-border/70 text-foreground hover:bg-muted',
                )}
                onClick={() => setReason(reason === value ? null : value)}
                role="radio"
                type="button"
              >
                {MESSAGE_FEEDBACK_REASON_LABELS[value]}
              </button>
            ))}
          </div>
          <Textarea
            aria-label="Précisions (facultatif)"
            className="min-h-16 text-sm"
            maxLength={MESSAGE_FEEDBACK_COMMENT_MAX_LENGTH}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Précisions (facultatif)"
            value={comment}
          />
          <div className="flex items-center justify-between gap-2">
            {isDown ? (
              <Button
                className="h-8 px-2 text-xs text-muted-foreground"
                onClick={() => {
                  feedback.clear(messageId);
                  setIsFormOpen(false);
                }}
                type="button"
                variant="ghost"
              >
                Retirer mon retour
              </Button>
            ) : <span />}
            <Button className="h-8 text-xs" onClick={submitNegative} type="button">
              Envoyer
            </Button>
          </div>
          <p className="text-[11px] leading-snug text-muted-foreground">
            Votre retour reste privé ; il sert à améliorer la recherche documentaire.
          </p>
        </PopoverContent>
      </Popover>
    </>
  );
}
